import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  DC_COLLECTION_TYPES,
  DcRosterConfigEvent,
  DcRosterConfigEventInput
} from '../../../domain/dc-roster-config.model';

export interface DcRosterConfigEventDialogData {
  mode: 'create' | 'edit';
  event?: DcRosterConfigEvent;
}

@Component({
  selector: 'app-dc-roster-config-event-dialog',
  templateUrl: './dc-roster-config-event-dialog.component.html',
  styleUrls: ['./dc-roster-config-event-dialog.component.css']
})
export class DcRosterConfigEventDialogComponent {
  readonly collectionTypes = DC_COLLECTION_TYPES;
  readonly values: DcRosterConfigEventInput;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DcRosterConfigEventDialogData,
    private dialogRef: MatDialogRef<
      DcRosterConfigEventDialogComponent,
      DcRosterConfigEventInput | null
    >
  ) {
    this.values = {
      code: data.event?.code ?? '',
      collectionType: data.event?.collectionType ?? 'Live',
      effort: data.event?.effort ?? 0,
      eventGrade: data.event?.eventGrade ?? 0
    };
  }

  /**
   * The backend stores codes uppercased and trimmed because every comparison
   * runs through the same normalization. Mirror it here so the field shows
   * what will actually be saved.
   */
  onCodeChanged(code: string): void {
    this.values.code = `${code ?? ''}`.toUpperCase();
  }

  get codeError(): string {
    const code = this.normalizedCode;
    if (code.length > 50) {
      return 'Code must be 50 characters or fewer';
    }
    if (/[.$|]/.test(code)) {
      return 'Code cannot contain ".", "$" or "|"';
    }
    return '';
  }

  get allValid(): boolean {
    return (
      !!this.normalizedCode &&
      !this.codeError &&
      this.collectionTypes.some(
        (collectionType) => collectionType === this.values.collectionType
      ) &&
      this.isNonNegativeNumber(this.values.effort) &&
      this.isNonNegativeNumber(this.values.eventGrade)
    );
  }

  close(): void {
    this.dialogRef.close(null);
  }

  submit(): void {
    if (!this.allValid) {
      return;
    }
    this.dialogRef.close({
      code: this.normalizedCode,
      collectionType: this.values.collectionType,
      effort: Number(this.values.effort),
      eventGrade: Number(this.values.eventGrade)
    });
  }

  private get normalizedCode(): string {
    return `${this.values.code ?? ''}`.trim().toUpperCase();
  }

  private isNonNegativeNumber(value: number | null): boolean {
    return (
      value != null && Number.isFinite(Number(value)) && Number(value) >= 0
    );
  }
}
